/**
 * `/system` — is the backend alive, and which build is it.
 *
 * `GET /api/health` touches nothing, including the database, so the green light
 * here means the process is answering and **not** that the database is
 * reachable — that is the separate row below it, read from the backend's own
 * ping. Saying so matters: an interface that claims "healthy" while every other
 * request fails sends people looking in the wrong place.
 *
 * The screen also lists what the deployment deliberately does not expose over
 * HTTP, because "where is the reload button" is otherwise a recurring question
 * with an invisible answer.
 */
import { useTranslation } from 'react-i18next';
import type { DatabaseStatus, ReleaseInfo, SchemaStatus } from '@forsys/shared';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TerminalIcon from '@mui/icons-material/Terminal';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AsyncContent } from '../components/AsyncContent';
import { Columns } from '../components/Columns';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Timestamp } from '../components/Timestamp';
import { useAsync } from '../hooks/useAsync';
import { getHealth } from '../api/health';

/**
 * How each schema state is coloured.
 *
 * A `Record` over the union rather than a chain of ternaries, so a fourth state
 * added to `SchemaStatus` is a compile error here instead of silently rendering
 * grey.
 */
const SCHEMA_COLOUR: Record<SchemaStatus['state'], 'success' | 'error' | 'default'> = {
  ok: 'success',
  pending: 'error',
  unknown: 'default',
};

/**
 * The label key per state, as data.
 *
 * A literal, not a template string built from the state: keys are type-checked
 * against the English catalogue, and an interpolated key gives the compiler
 * nothing to check. Same reason `NAVIGATION` and `CLI_ONLY` hold keys — see
 * ../i18n/README.md.
 */
const SCHEMA_LABEL_KEY = {
  ok: 'system:system.schema.ok',
  pending: 'system:system.schema.pending',
  unknown: 'system:system.schema.unknown',
} as const satisfies Record<SchemaStatus['state'], string>;

/** The same two tables for the database ping, for the same reasons. */
const DATABASE_COLOUR: Record<DatabaseStatus['state'], 'success' | 'error' | 'default'> = {
  ok: 'success',
  unreachable: 'error',
  unknown: 'default',
};

const DATABASE_LABEL_KEY = {
  ok: 'system:system.database.ok',
  unreachable: 'system:system.database.unreachable',
  unknown: 'system:system.database.unknown',
} as const satisfies Record<DatabaseStatus['state'], string>;

/**
 * The operator actions that stay at the terminal, and why.
 *
 * The command is an identifier and stays as it is typed; only the explanation
 * beside it is a key, resolved at render time rather than baked in here — this
 * is module-level data, so a translated string would freeze at import.
 */
const CLI_ONLY = [
  ['forsys config:show', 'system.cliOnly.configShow'],
  ['forsys config:reload', 'system.cliOnly.configReload'],
  ['forsys db:migrate', 'system.cliOnly.dbMigrate'],
  ['forsys retention:sweep', 'system.cliOnly.retentionSweep'],
] as const;

export default function SystemPage() {
  const { t } = useTranslation(['system', 'common']);
  const health = useAsync(() => getHealth(), []);

  /**
   * `v42 – Aero (2026-10-21-123)`, the words the CLI and the chat message use.
   * The number, name and build id are identifiers and stay untranslated; only
   * a build the release pipeline did not make gets a translated label.
   */
  function formatRelease(release: ReleaseInfo): string {
    if (release.number === null) return t('system:system.release.dev');
    const name = release.name === null ? '' : ` – ${release.name}`;
    return `v${release.number}${name} (${release.build ?? '?'})`;
  }

  /**
   * 93784 → "1d 2h 3m". Seconds are noise at any uptime worth looking at.
   *
   * Inside the component because the unit suffixes are translated, so it needs
   * a `t` — and a language change has to move them.
   */
  function formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts: string[] = [];
    if (days > 0) parts.push(t('system:system.uptimeDays', { count: days }));
    if (hours > 0) parts.push(t('system:system.uptimeHours', { count: hours }));
    parts.push(t('system:system.uptimeMinutes', { count: minutes }));
    return parts.join(' ');
  }

  return (
    <>
      <PageHeader
        title={t('system:system.title')}
        description={t('system:system.description')}
        actions={
          <Button startIcon={<RefreshIcon />} onClick={() => health.reload()} disabled={health.loading}>
            {t('common:action.refresh')}
          </Button>
        }
      />

      <Columns>
        <Panel title={t('system:system.backend')}>

          <AsyncContent state={health} placeholder="spinner">
            {(status) => (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6">{t('system:system.answering')}</Typography>
                  <Chip size="small" label={formatRelease(status.release)} variant="outlined" />
                </Stack>

                <Table size="small">
                  <TableBody>
                    {status.release.commit !== null && (
                      <TableRow>
                        <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                          {t('system:system.release.commit')}
                        </TableCell>
                        <TableCell sx={{ border: 0, fontFamily: 'monospace' }} title={status.release.commit}>
                          {status.release.commit.slice(0, 12)}
                        </TableCell>
                      </TableRow>
                    )}
                    {status.release.builtAt !== null && (
                      <TableRow>
                        <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                          {t('system:system.release.built')}
                        </TableCell>
                        <TableCell sx={{ border: 0 }}>
                          <Timestamp value={status.release.builtAt} absolute />
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                        {t('system:system.uptime')}
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>{formatUptime(status.uptime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                        {t('system:system.responded')}
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Timestamp value={status.timestamp} absolute />
                      </TableCell>
                    </TableRow>
                    {/* The database and schema states ride along on the probe,
                        but they are different questions with different answers:
                        the process can be perfectly alive with its database
                        down, or on top of a schema three migrations behind.
                        That is the whole reason they are here rather than
                        folded into the green light above. */}
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                        {t('system:system.database.label')}
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Chip
                          size="small"
                          color={DATABASE_COLOUR[status.database.state]}
                          variant={status.database.state === 'ok' ? 'outlined' : 'filled'}
                          label={t(DATABASE_LABEL_KEY[status.database.state])}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                        {t('system:system.schema.label')}
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Chip
                          size="small"
                          color={SCHEMA_COLOUR[status.schema.state]}
                          variant={status.schema.state === 'ok' ? 'outlined' : 'filled'}
                          label={t(SCHEMA_LABEL_KEY[status.schema.state], {
                            count: status.schema.pendingCount,
                          })}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                        {t('system:system.schema.checked')}
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        {status.schema.checkedAt === null ? (
                          t('system:system.schema.never')
                        ) : (
                          <Timestamp value={status.schema.checkedAt} absolute />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Alert severity="info" icon={false}>
                  {t('system:system.probeNote')}
                </Alert>

                <Alert severity="info" icon={false}>
                  {t('system:system.schema.note')}
                </Alert>
              </Stack>
            )}
          </AsyncContent>
        </Panel>

        <Panel icon={<TerminalIcon fontSize="small" />} title={t('system:system.cliOnly.title')}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('system:system.cliOnly.why')}
          </Typography>

          <Stack spacing={1.5}>
            {CLI_ONLY.map(([command, whatKey]) => (
              <Box key={command}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {command}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(whatKey)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Panel>
      </Columns>
    </>
  );
}
